import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { endOfQuarter, format, startOfQuarter, startOfToday } from "date-fns";
import { Category, LocationScope, TimeScope } from "generated/graphql";
import i18n, { LocalKeys, useTranslation } from "utilities/i18n";
import {
  DATE_FORMAT,
  Topic,
  TopicGroup,
  topicGroupsByCategory,
  topicsByGroup
} from "Constants";
import {
  DropdownButton,
  DropdownList,
  DropdownListGroup,
  LoadingIndicator,
  PolyIcon,
  PolyIconButton,
  PremiumBetaBadge,
  ToolTip,
  useFeatureFlagWithTenant
} from "elements";
import { ComboChart } from "elements/Charts/ComboChart";
import { Formik } from "collections";
import { Section } from "collections/Section";
import { Card } from "collections/Cards";
import { truncIndicator } from "collections/InsightDetails";
import { TimeScopePicker } from "../TimeScopePicker/TimeScopePicker";
import { useAnalysisChartsData } from "./useAnalysisChartsData";
import {
  ActionType,
  ChartOptions,
  useAnalysisChartsReducer
} from "./useAnalysisChartsReducer";
import "./AnalysisCharts.scss";

const getDateString = (scope?: TimeScope, timeStart?: Date) => {
  if (!scope || !timeStart) {
    return "";
  }

  switch (scope) {
    case TimeScope.Year:
      return format(timeStart, DATE_FORMAT.YEAR);
    case TimeScope.Quarter:
      const start = startOfQuarter(timeStart);
      const end = endOfQuarter(timeStart);
      return `${format(start, DATE_FORMAT.MONTH_SHORT)} - ${format(
        end,
        DATE_FORMAT.MONTH_SHORT_YEAR
      )}`;
    case TimeScope.Month:
      return format(timeStart, DATE_FORMAT.MONTH_SHORT_YEAR);
    default:
      return format(timeStart, DATE_FORMAT.MONTH_SHORT_DATE_YEAR);
  }
};

const getTitleTranslation = (topic: Topic) => {
  const key: LocalKeys = `ANALYSIS_TOPIC_${topic}_TITLE` as const;
  return i18n.t(key);
};

const getIndicatorTranslation = (topic: Topic, value?: string) => {
  const key: LocalKeys = `ANALYSIS_TOPIC_${topic}_INDICATOR` as const;
  return i18n.t(key, { value });
};

const getDescriptionTranslation = (topic: Topic) => {
  const key: LocalKeys = `ANALYSIS_TOPIC_${topic}_DESCRIPTION` as const;
  return i18n.t(key);
};

const TopicDropdown: React.FC<{
  className: string;
  topicGroup: TopicGroup;
  currentTopic?: Topic;
  onChange: (options: Partial<ChartOptions>) => void;
}> = ({ className, topicGroup, currentTopic, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className={className}>
      <DropdownListGroup>
        <label
          className="type-label selected letter-space-2 text-uppercase"
          htmlFor="topics-select"
        >
          {t("INSIGHT")}
        </label>
        <DropdownList
          className="lg box-shadow mt-5 border-radius-4"
          name="topics"
          testid={topicGroup}
          value={currentTopic}
          placeholder={t("SELECT")}
          options={topicsByGroup[topicGroup].map(topic => ({
            value: topic,
            text: getTitleTranslation(topic)
          }))}
          onChange={value => onChange({ topic: value as Topic })}
        />
      </DropdownListGroup>
    </div>
  );
};

const TimeScopeDropdown: React.FC<{
  className: string;
  timeScope: TimeScope;
  timeStart: Date;
  onChange: (options: Partial<ChartOptions>) => void;
}> = ({ className, timeScope, timeStart, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className={className} data-testid="timescope-dropdown">
      <DropdownListGroup>
        <label
          className="type-label selected letter-space-2 text-uppercase"
          htmlFor="date-select"
        >
          {t(timeScope || "DATE_RANGE")}
        </label>
        <div className="dropdown-list md mt-5 box-shadow border-radius-4">
          <TimeScopePicker
            timeScope={timeScope || TimeScope.Year}
            timeStart={timeStart || startOfToday()}
            onSubmit={(timeScope, timeStart) =>
              onChange({ timeScope, timeStart })
            }
          >
            {togglePicker => (
              <div
                className="input-wrapper"
                data-testid="timescope-dropdown-list"
              >
                <span
                  className="toggle-area"
                  data-testid="timescope-dropdown-input"
                >
                  <input
                    type="text"
                    placeholder={t("SELECT")}
                    value={getDateString(timeScope, timeStart)}
                    onClick={togglePicker}
                    readOnly
                    data-testid="timescope-input-element"
                  />
                </span>
                <span className="icon" onClick={togglePicker}>
                  <PolyIcon icon="keyboard_arrow_down" />
                </span>
              </div>
            )}
          </TimeScopePicker>
        </div>
      </DropdownListGroup>
    </div>
  );
};

const ChartLoader: React.FC<{
  locationId: string;
  locationScope: LocationScope;
  category: Category;
  options: ChartOptions;
}> = ({ locationId, locationScope, category, options }) => {
  const { topic, timeScope } = options;
  const { t } = useTranslation();
  const {
    chartSeries,
    chartMeta,
    topicIndicator,
    theme,
    colors,
    fetching
  } = useAnalysisChartsData(locationId, locationScope, options);
  const [isAggregateValueEnabled] = useFeatureFlagWithTenant(
    "ANALYSIS_CHARTS_AGGREGATE_VALUE"
  );

  if (fetching) {
    return <LoadingIndicator />;
  }

  const indicatorValue = truncIndicator(topicIndicator?.indicator)?.toString();

  const countValue = chartMeta?.childCount;
  const countUnits = `${category}${countValue === 1 ? "" : "_other"}` as const;

  return (
    <>
      {isAggregateValueEnabled && (
        <div className="d-flex align-center">
          <h4 className="mr-5 text-lowercase">
            {getIndicatorTranslation(topic, indicatorValue)}
          </h4>
          <ToolTip
            className="font-xs"
            icon="info_circle"
            iconTitle=""
            position="bottom"
          >
            {getDescriptionTranslation(topic)}
          </ToolTip>
        </div>
      )}
      <ComboChart
        series={chartSeries}
        colors={colors}
        timeScope={timeScope}
        theme={theme}
        height={300}
      />
      {isAggregateValueEnabled && countValue !== undefined && (
        <div className="count-overlay text-right font-xs">
          <strong>{countValue}</strong>
          <span className="opacity-50"> {t(countUnits)}</span>
        </div>
      )}
    </>
  );
};

const ChartCard: React.FC<{
  topicGroup: TopicGroup;
  locationId: string;
  locationScope: LocationScope;
  category: Category;
  options: ChartOptions;
  updateChart: (options: Partial<ChartOptions>) => void;
  duplicateChart: () => void;
  removeChart: () => void;
}> = ({
  topicGroup,
  locationId,
  locationScope,
  category,
  options,
  updateChart,
  duplicateChart,
  removeChart
}) => {
  const { topic, timeScope, timeStart, minimized } = options;
  const { t } = useTranslation();

  /** Below hooks can be deleted once feature flag is removed */
  const [isAggregateValueEnabled] = useFeatureFlagWithTenant(
    "ANALYSIS_CHARTS_AGGREGATE_VALUE"
  );

  return (
    <Card className="mb-1 box-shadow">
      <div
        className={`d-flex align-start ${
          !isAggregateValueEnabled && !minimized ? "pt-15 ph-15" : "p-15"
        }`}
      >
        <TopicDropdown
          className="mr-15"
          topicGroup={topicGroup}
          currentTopic={topic}
          onChange={updateChart}
        />
        <TimeScopeDropdown
          className={!isAggregateValueEnabled ? "mr-1" : "flex-1 mr-15"}
          timeScope={timeScope}
          timeStart={timeStart}
          onChange={updateChart}
        />
        {/** Tooltip can be deleted here once feature flag is removed */
        !isAggregateValueEnabled && (
          <div className="flex-1 v-bottom">
            <ToolTip
              className="font-xs pb-5"
              icon="info_circle"
              iconTitle=""
              position="bottom"
            >
              {getDescriptionTranslation(topic)}
            </ToolTip>
          </div>
        )}
        <DropdownButton
          className="font-md mr-1"
          icon="more_menu"
          iconClassName="blue"
          title={t("MORE_OPTIONS")}
          aria-label={t("MORE_OPTIONS")}
        >
          <div
            className="clickable pv-5 pl-15 pr-2 mt-5"
            onClick={duplicateChart}
          >
            {t("DUPLICATE")}
          </div>
          <div className="clickable pv-5 pl-15 pr-2 mb-5" onClick={removeChart}>
            {t("REMOVE")}
          </div>
        </DropdownButton>
        <PolyIconButton
          className="white"
          icon={minimized ? "maximize" : "minimize"}
          title={t(minimized ? "SHOW_CHART" : "HIDE_CHART")}
          aria-label={t(minimized ? "SHOW_CHART" : "HIDE_CHART")}
          onClick={() => updateChart({ minimized: !minimized })}
        />
      </div>
      <div
        className={
          `chart-container ph-15${minimized ? " minimized" : ""}` +
          /** Can be deleted once feature flag is removed */
          `${!isAggregateValueEnabled ? " aggregate-hidden" : ""}`
        }
        data-testid="chart-container"
      >
        <ChartLoader
          locationId={locationId}
          locationScope={locationScope}
          category={category}
          options={options}
        />
      </div>
    </Card>
  );
};

const AddChartCard: React.FC<{
  topicGroup: TopicGroup;
  addChart: (options: ChartOptions) => void;
}> = ({ topicGroup, addChart }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const AddChartFormSchema = yup.object().shape({
    topic: yup
      .mixed<Topic>()
      .oneOf(topicsByGroup[topicGroup])
      .required(),
    timeScope: yup
      .mixed<TimeScope>()
      .oneOf(Object.values(TimeScope))
      .required(),
    timeStart: yup.date().required()
  });
  type AddChartForm = yup.InferType<typeof AddChartFormSchema>;

  // Set the initial value of all fields to undefined.
  // "as AddChartForm" needed since undefined is not allowed in the validation schema.
  const initialValues = {} as AddChartForm;

  return (
    <Card className={`add-chart mb-0 box-shadow${isOpen ? "" : " collapsed"}`}>
      {isOpen ? (
        <Formik
          name="TimeScopePicker"
          initialValues={initialValues}
          validationSchema={AddChartFormSchema}
          onSubmit={(values: AddChartForm) => {
            addChart({ ...values, minimized: false });
            setIsOpen(false);
          }}
          onReset={() => setIsOpen(false)}
          validateOnChange={true}
        >
          {({ values, setFieldValue, submitForm, resetForm }) => (
            <div className="d-flex align-center p-15">
              <TopicDropdown
                className="mr-15"
                topicGroup={topicGroup}
                currentTopic={values.topic}
                onChange={({ topic }) => setFieldValue("topic", topic, true)}
              />
              <TimeScopeDropdown
                className="flex-1 mr-15"
                timeScope={values.timeScope}
                timeStart={values.timeStart}
                onChange={({ timeScope, timeStart }) => {
                  setFieldValue("timeScope", timeScope, true);
                  setFieldValue("timeStart", timeStart, true);
                }}
              />
              <button
                className="btn ph-1 pv-5"
                type="button"
                onClick={() => resetForm()}
              >
                {t("CANCEL")}
              </button>
              <button
                className="btn btn-primary ph-1 pv-5 ml-1"
                type="button"
                onClick={submitForm}
                disabled={!values.topic || !values.timeScope}
              >
                {t("ADD")}
              </button>
            </div>
          )}
        </Formik>
      ) : (
        <div className="d-flex p-1">
          <span
            className="d-flex align-center clickable"
            role="button"
            onClick={() => setIsOpen(true)}
          >
            <PolyIcon icon="plus_circle" className="white pr-5" />
            {t("ADD_CHART")}
          </span>
        </div>
      )}
    </Card>
  );
};

const AnalysisChartSection: React.FC<{
  topicGroup: TopicGroup;
  id: string;
  locationScope: LocationScope;
  category: Category;
}> = ({ topicGroup, id, locationScope, category }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useAnalysisChartsReducer(topicGroup);

  return (
    <Section className="mb-15" key={topicGroup}>
      <div className="mb-1 d-flex">
        <h5>{t(topicGroup)}</h5>
        {category === Category.Room && <PremiumBetaBadge />}
      </div>
      <div className="ml-15">
        {state.charts.map((chartOptions, index) => (
          <ChartCard
            key={`chart-${index}`}
            options={chartOptions}
            topicGroup={topicGroup}
            locationScope={locationScope}
            locationId={id}
            category={category}
            updateChart={(options: Partial<ChartOptions>) => {
              dispatch({
                type: ActionType.UpdateChart,
                payload: { index, options }
              });
            }}
            duplicateChart={() =>
              dispatch({
                type: ActionType.DuplicateChart,
                payload: { index }
              })
            }
            removeChart={() =>
              dispatch({
                type: ActionType.RemoveChart,
                payload: { index }
              })
            }
          />
        ))}
        <AddChartCard
          topicGroup={topicGroup}
          addChart={(options: ChartOptions) => {
            dispatch({
              type: ActionType.AddChart,
              payload: { options }
            });
          }}
        />
      </div>
    </Section>
  );
};

export const AnalysisCharts: React.FC<{
  id: string;
  locationScope: LocationScope;
  category: Category;
}> = ({ id, locationScope, category }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current?.scrollTop) {
      ref.current.scrollTo(0, 0);
    }
  }, [ref, id, category, locationScope]);

  return (
    <div className="analysis-charts scrollable" ref={ref}>
      {topicGroupsByCategory[category].map(topicGroup => (
        <AnalysisChartSection
          key={topicGroup}
          {...{ id, locationScope, category, topicGroup }}
        />
      ))}
    </div>
  );
};
